export enum Seasons {
  Fall = `Fall`,
  Winter = `Winter`,
  Spring = `Spring`,
  Summer = `Summer`,
}

export type SeasonDescription = {
  season: Seasons;
  translationKey: string;
  months: number[]; // month numbers (1 = January, 12 = December)
};

export const SeasonDescriptions: SeasonDescription[] = [
  {
    season: Seasons.Winter,
    translationKey: `Courses.importantDates.seasons.winter`,
    months: [],
  },
  {
    season: Seasons.Spring,
    translationKey: `Courses.importantDates.seasons.spring`,
    months: [1, 2, 3, 4], // January to April
  },
  {
    season: Seasons.Summer,
    translationKey: `Courses.importantDates.seasons.summer`,
    months: [5, 6, 7], // May to July
  },
  {
    season: Seasons.Fall,
    translationKey: `Courses.importantDates.seasons.fall`,
    months: [8, 9, 10, 11, 12], // August to November
  },
];

export const SeasonDescriptionsOSU: SeasonDescription[] = [
  {
    season: Seasons.Winter,
    translationKey: `Courses.importantDates.seasons.winter`,
    months: [12, 1, 2], // December to February
  },
  {
    season: Seasons.Spring,
    translationKey: `Courses.importantDates.seasons.spring`,
    months: [3, 4, 5], // March to May
  },
  {
    season: Seasons.Summer,
    translationKey: `Courses.importantDates.seasons.summer`,
    months: [6, 7, 8], // June to August
  },
  {
    season: Seasons.Fall,
    translationKey: `Courses.importantDates.seasons.fall`,
    months: [9, 10, 11], // September to November
  },
];

function getSeasonDescriptions(isOSU: boolean) {
  return isOSU ? SeasonDescriptionsOSU : SeasonDescriptions;
}

function dateDiffMonths(dateFrom: Date, dateTo: Date) {
  return (
    dateTo.getMonth() - dateFrom.getMonth() + 12 * (dateTo.getFullYear() - dateFrom.getFullYear())
  );
}

const USUAL_SEASON_MONTHS = 3;

// Determines a season which has more months during the period from startDate to endDate
export function getPrevalentSeasonDescription(startDate: Date, endDate: Date, isOSU: boolean) {
  const descriptions = getSeasonDescriptions(isOSU);

  if (startDate > endDate) {
    let tempDate = startDate;
    startDate = endDate;
    endDate = tempDate;
  }

  const oneYearFromStart = new Date(startDate);
  oneYearFromStart.setFullYear(oneYearFromStart.getFullYear() + 1);

  let adjustedEndDate = endDate;
  if (endDate > oneYearFromStart) {
    adjustedEndDate = oneYearFromStart;
  }

  // Typical season lasts at least 4 months
  if (dateDiffMonths(startDate, adjustedEndDate) > USUAL_SEASON_MONTHS) {
    adjustedEndDate = startDate;

    // -1 to exclude first month of startDate from calculation
    let adjustedEndMonth = adjustedEndDate.getMonth() + USUAL_SEASON_MONTHS - 1;
    adjustedEndDate.setMonth(adjustedEndMonth);
  }

  let prevalentSeason: SeasonDescription | null = null;
  let maxCount = 0;

  for (const season of descriptions) {
    if (!season.months.length) {
      continue;
    }

    let count = 0;
    let currentDate = new Date(startDate);

    while (currentDate <= adjustedEndDate) {
      const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, we need 1-12

      if (season.months.includes(currentMonth)) {
        count++;
      }

      // Move to the next month
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    if (count > maxCount) {
      maxCount = count;
      prevalentSeason = season;
    }
  }

  return prevalentSeason;
}
